import React from 'react';
import LegalLayout from '../components/LegalLayout';

const RefundPolicy = () => (
  <LegalLayout title="Refund &amp; Cancellation Policy" lastUpdated="August 24, 2026">
    <p>
      This policy describes how refunds and cancellations work for digital products and subscriptions on Create by Lumera (<strong>createbylumera.com</strong>), operated by Create by Lumera.
    </p>

    <h2>1. Individual Digital Product Purchases</h2>

    <h3>1.1 Nature of Digital Products</h3>
    <p>
      Lumera products are digital goods. Upon confirmed payment, you receive immediate access to create, customize, generate and download your purchased product. Because of this immediate access and the digital nature of the product, purchases are generally <strong>non-refundable</strong> once the product has been accessed, generated or downloaded.
    </p>

    <h3>1.2 Pre-Purchase Acknowledgment</h3>
    <p>
      Before completing a digital product purchase, you will be asked to acknowledge that:
    </p>
    <ul>
      <li>You are requesting immediate access to and delivery of digital content.</li>
      <li>By receiving immediate access, you understand that you may lose your statutory right of withdrawal where applicable under your local consumer protection laws.</li>
      <li>You have reviewed and accepted this Refund Policy and our Terms of Service.</li>
    </ul>

    <h3>1.3 Exceptions</h3>
    <p>
      We evaluate refund requests on a case-by-case basis and may grant a refund where:
    </p>
    <ul>
      <li><strong>Technical failure:</strong> The product failed to generate, could not be accessed, or was not delivered due to a platform error, and we are unable to resolve the issue within a reasonable timeframe.</li>
      <li><strong>Duplicate or accidental charge:</strong> You were charged more than once for the same product, or the charge was clearly accidental.</li>
      <li><strong>Product not as described:</strong> The delivered product materially differs from what was described at the time of purchase.</li>
      <li><strong>Applicable consumer law:</strong> A refund is required under mandatory consumer protection legislation in your jurisdiction.</li>
    </ul>

    <h3>1.4 How to Request a Refund</h3>
    <p>
      Contact us at <a href="mailto:hello@createbylumera.com">hello@createbylumera.com</a> within 14 days of purchase, including your order details and reason for the request. We aim to respond within 3 business days.
    </p>

    <h2>2. Lumera Pro Subscriptions</h2>

    <h3>2.1 How to Cancel</h3>
    <p>
      You can cancel your Pro subscription at any time from your <a href="/profile">Profile page</a>. Cancellation takes effect at the end of your current billing period. You continue to have full Pro access until that date.
    </p>

    <h3>2.2 What Happens When You Cancel</h3>
    <ul>
      <li><strong>No further charges:</strong> Your next recurring payment is stopped.</li>
      <li><strong>Continued access:</strong> You retain full Pro access until the end of your current billing period.</li>
      <li><strong>Automatic downgrade:</strong> After your billing period ends, your account reverts to the free plan.</li>
      <li><strong>Commercial licenses preserved:</strong> Any Commercial Licenses you earned for products created while Pro was active remain valid permanently.</li>
    </ul>

    <h3>2.3 Subscription Refunds</h3>
    <p>
      We generally do not issue partial refunds for unused subscription time. However, we may consider a refund where:
    </p>
    <ul>
      <li>You were charged after having cancelled (processing error).</li>
      <li>A technical issue prevented you from using Pro features during a significant portion of the billing period, and we were unable to resolve it.</li>
      <li>A refund is required under mandatory consumer protection legislation in your jurisdiction.</li>
    </ul>

    <h3>2.4 Reactivation</h3>
    <p>
      If you cancel but change your mind before the billing period ends, you can reactivate your subscription from the same Profile page. Reactivating resumes automatic renewal with no gap in service.
    </p>

    <h2>3. Consumer Rights</h2>
    <p>
      Nothing in this policy is intended to limit or exclude any mandatory consumer rights that cannot legally be waived under the applicable laws of your jurisdiction. Where local consumer protection law provides greater protection than this policy, the applicable law will prevail.
    </p>

    <h2>4. Contact</h2>
    <p>
      For refund requests or questions about this policy, contact us at <a href="mailto:hello@createbylumera.com">hello@createbylumera.com</a>.
    </p>
  </LegalLayout>
);

export default RefundPolicy;
